/**
 * Fund-investor account statement -- pure data/HTML builders.
 *
 * Extracted from src/shared_assets/index.js's Account Statement subsystem (#5447
 * componentization pass). Used for clients on the fund_investor_capital_account/
 * tvpi_fund calculator methods (e.g. gpgventures, maguirehayden). Called from
 * renderFundAccountStatement() (which stays in index.js since it owns the
 * loading-modal/PDF-download DOM wiring).
 *
 * clientData/window.lastCalculationResult are read as bare/window globals here,
 * matching the window-bridge pattern the rest of this codebase uses (see
 * CLAUDE.md's AppState bridge) -- not passed as parameters, matching every
 * other already-extracted helper in this directory.
 */
import moment from 'moment';
import { escapeStatementHtml } from './account-statement-shared.js';

export function buildFundStatementData(targetUserData) {
	const calcResult = window.lastCalculationResult || {};

	return {
		statementDate: moment().format('MMMM D, YYYY'),
		userName: targetUserData.user_name?.replace(/_/g, ' ') || '',
		totalValue: calcResult.totalValue || 0,
		totalContributions: calcResult.totalContributions || 0,
		totalDistributions: calcResult.totalDistributions || 0,
		totalCapitalAccount: calcResult.totalCapitalAccount || 0,
		totalReturnsOfCapital: calcResult.totalReturnsOfCapital || 0,
		breakdown: calcResult.breakdown || [],
		fundMetrics: calcResult.fundMetrics || {},
	};
}

export function buildFundStatementHtml(statementData) {
	const textColor = '#243b53';
	const fmtCurrency = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
	const fmtMultiple = (v) => Number(v || 0).toFixed(2) + 'x';

	let html = `
	<!DOCTYPE html>
	<html>
	<head>
		<style>
			body {
				font-family: 'Source Sans Pro', Arial, sans-serif;
				font-size: 12px;
				margin: 0;
				padding: 20px;
				color: #243b53;
			}
			.summary-section {
				page-break-inside: avoid;
				background: #f0f4f8;
				border-radius: 8px;
				padding: 20px;
				margin-bottom: 30px;
			}
			.summary-grid {
				display: flex;
				justify-content: space-between;
				flex-wrap: wrap;
			}
			.summary-item {
				text-align: center;
				flex: 1;
				min-width: 120px;
				padding: 10px;
			}
			.summary-label {
				font-size: 10px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 5px;
			}
			.summary-value {
				font-size: 14px;
				font-weight: bold;
				color: #243b53;
			}
			.summary-value.large {
				font-size: 24px;
				color: #1a5276;
			}
			.section-title {
				font-size: 16px;
				font-weight: bold;
				color: ${textColor};
				margin-bottom: 15px;
				border-bottom: 2px solid #1a5276;
				padding-bottom: 5px;
			}
			.holdings-section {
				margin-bottom: 30px;
			}
			table.breakdown {
				width: 100%;
				border-collapse: collapse;
				font-size: 11px;
			}
			table.breakdown th {
				background-color: #1a5276;
				color: white;
				padding: 10px 8px;
				text-align: right;
				font-weight: bold;
				font-size: 10px;
			}
			table.breakdown th:first-child {
				text-align: left;
			}
			table.breakdown td {
				padding: 8px;
				border-bottom: 1px solid #e2e8f0;
				text-align: right;
			}
			table.breakdown td:first-child {
				text-align: left;
				font-weight: bold;
			}
			table.breakdown tr:nth-child(even) {
				background-color: #f8fafc;
			}
			table.breakdown tfoot td {
				font-weight: bold;
				border-top: 2px solid #1a5276;
				background: #f0f4f8;
			}
		</style>
	</head>
	<body>
	`;

	// Summary block: Statement Date + Total Value
	html += `
		<div class="summary-section">
			<div class="summary-grid">
				<div class="summary-item">
					<div class="summary-label">Statement Date</div>
					<div class="summary-value">${statementData.statementDate}</div>
				</div>
				<div class="summary-item">
					<div class="summary-label">Total Value</div>
					<div class="summary-value large">${fmtCurrency(statementData.totalValue)}</div>
				</div>
			</div>
		</div>
	`;

	// Account Metrics
	html += `
		<div style="display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0;">
			<div style="text-align: center; flex: 1;">
				<div class="summary-label">Capital Account</div>
				<div class="summary-value">${fmtCurrency(statementData.totalCapitalAccount)}</div>
			</div>
			<div style="text-align: center; flex: 1;">
				<div class="summary-label">Contributions</div>
				<div class="summary-value">${fmtCurrency(statementData.totalContributions)}</div>
			</div>
			<div style="text-align: center; flex: 1;">
				<div class="summary-label">Distributions</div>
				<div class="summary-value">${fmtCurrency(statementData.totalDistributions)}</div>
			</div>
			<div style="text-align: center; flex: 1;">
				<div class="summary-label">Returns of Capital</div>
				<div class="summary-value">${fmtCurrency(statementData.totalReturnsOfCapital)}</div>
			</div>
			<div style="text-align: center; flex: 1;">
				<div class="summary-label">TVPI</div>
				<div class="summary-value">${fmtMultiple(statementData.fundMetrics.tvpi)}</div>
			</div>
		</div>
	`;

	// 2-bar comparison chart: Contributions vs Total Value
	if (statementData.totalContributions > 0 || statementData.totalValue > 0) {
		const chartSvg = buildFundComparisonChartSvg(statementData);
		if (chartSvg) {
			html += `
				<div class="holdings-section">
					<div class="section-title">Paid-In Capital vs Total Value</div>
					<div style="padding: 10px 0;">
						${chartSvg}
					</div>
				</div>
			`;
		}
	}

	// Holdings by Fund/Class breakdown table
	if (statementData.breakdown.length > 0) {
		html += `
			<div class="holdings-section">
				<div class="section-title">Holdings by Fund</div>
				<table class="breakdown">
					<thead>
						<tr>
							<th>Fund / Class</th>
							<th>Contributions</th>
							<th>Distributions</th>
							<th>Returns of Capital</th>
							<th>Capital Account</th>
							<th>Total Value</th>
							<th>TVPI</th>
						</tr>
					</thead>
					<tbody>
		`;

		statementData.breakdown.forEach((row) => {
			html += `
						<tr>
							<td>${escapeStatementHtml(row.className)}</td>
							<td>${fmtCurrency(row.contributions)}</td>
							<td>${fmtCurrency(row.distributions)}</td>
							<td>${fmtCurrency(row.returnsOfCapital)}</td>
							<td>${fmtCurrency(row.capitalAccount)}</td>
							<td>${fmtCurrency(row.value)}</td>
							<td>${fmtMultiple(row.tvpi)}</td>
						</tr>
			`;
		});

		html += `
					</tbody>
					<tfoot>
						<tr>
							<td>Total</td>
							<td>${fmtCurrency(statementData.totalContributions)}</td>
							<td>${fmtCurrency(statementData.totalDistributions)}</td>
							<td>${fmtCurrency(statementData.totalReturnsOfCapital)}</td>
							<td>${fmtCurrency(statementData.totalCapitalAccount)}</td>
							<td>${fmtCurrency(statementData.totalValue)}</td>
							<td>${fmtMultiple(statementData.fundMetrics.tvpi)}</td>
						</tr>
					</tfoot>
				</table>
			</div>
		`;
	}

	html += `
	</body>
	</html>
	`;

	return html;
}

export function buildFundComparisonChartSvg(statementData) {
	const width = 700;
	const height = 250;
	const barWidth = 140;
	const gap = 100;
	const maxVal = Math.max(statementData.totalContributions, statementData.totalValue, 1);
	const chartTop = 45;
	const chartBottom = height - 50;
	const chartHeight = chartBottom - chartTop;
	const centerX = width / 2;
	const r = 4; // border radius

	const bar1X = centerX - gap / 2 - barWidth;
	const bar2X = centerX + gap / 2;

	const bar1Height = Math.max((statementData.totalContributions / maxVal) * chartHeight, 2);
	const bar2Height = Math.max((statementData.totalValue / maxVal) * chartHeight, 2);

	const fmtVal = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

	// Stacked segments for Total Value bar
	const capAcct = statementData.totalCapitalAccount || 0;
	const distrib = statementData.totalDistributions || 0;
	const roc = statementData.totalReturnsOfCapital || 0;
	const totalVal = statementData.totalValue || 1;

	const capAcctH = (capAcct / totalVal) * bar2Height;
	const distribH = (distrib / totalVal) * bar2Height;
	const rocH = (roc / totalVal) * bar2Height;

	// Gain calculation
	const gain = statementData.totalValue - statementData.totalContributions;
	const gainPct = statementData.totalContributions > 0 ? ((gain / statementData.totalContributions) * 100).toFixed(1) : 0;
	const gainSign = gain >= 0 ? '+' : '';

	const legendY = chartBottom + 32;

	return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="width:100%;max-width:${width}px;">
		<defs>
			<clipPath id="bar1Clip"><rect x="${bar1X}" y="${chartBottom - bar1Height}" width="${barWidth}" height="${bar1Height}" rx="${r}"/></clipPath>
			<clipPath id="bar2Clip"><rect x="${bar2X}" y="${chartBottom - bar2Height}" width="${barWidth}" height="${bar2Height}" rx="${r}"/></clipPath>
		</defs>

		<!-- Bar 1: Contributions -->
		<rect x="${bar1X}" y="${chartBottom - bar1Height}" width="${barWidth}" height="${bar1Height}" fill="#90a4ae" clip-path="url(#bar1Clip)"/>
		<text x="${bar1X + barWidth / 2}" y="${chartBottom - bar1Height - 10}" text-anchor="middle" font-size="13" font-weight="bold" fill="#243b53">${fmtVal(statementData.totalContributions)}</text>
		<text x="${bar1X + barWidth / 2}" y="${chartBottom + 18}" text-anchor="middle" font-size="11" font-weight="bold" fill="#243b53">Paid-In Capital</text>

		<!-- Bar 2: Total Value (stacked segments clipped to rounded rect) -->
		<g clip-path="url(#bar2Clip)">
			<rect x="${bar2X}" y="${chartBottom - capAcctH}" width="${barWidth}" height="${capAcctH}" fill="#1a5276"/>
			<rect x="${bar2X}" y="${chartBottom - capAcctH - distribH}" width="${barWidth}" height="${distribH}" fill="#2980b9"/>
			<rect x="${bar2X}" y="${chartBottom - bar2Height}" width="${barWidth}" height="${rocH}" fill="#5dade2"/>
		</g>
		<text x="${bar2X + barWidth / 2}" y="${chartBottom - bar2Height - 10}" text-anchor="middle" font-size="13" font-weight="bold" fill="#243b53">${fmtVal(statementData.totalValue)}</text>
		<text x="${bar2X + barWidth / 2}" y="${chartBottom + 18}" text-anchor="middle" font-size="11" font-weight="bold" fill="#243b53">Total Value</text>

		<!-- Net return annotation (centered between bars) -->
		<text x="${centerX}" y="${chartTop - 18}" text-anchor="middle" font-size="11" fill="#486581">${gainSign}${fmtVal(Math.abs(gain))} (${gainSign}${gainPct}%) net return</text>

		<!-- Legend -->
		<rect x="${centerX - 180}" y="${legendY}" width="10" height="10" fill="#1a5276" rx="2"/>
		<text x="${centerX - 165}" y="${legendY + 9}" font-size="10" fill="#666">Capital Account</text>
		<rect x="${centerX - 60}" y="${legendY}" width="10" height="10" fill="#2980b9" rx="2"/>
		<text x="${centerX - 45}" y="${legendY + 9}" font-size="10" fill="#666">Distributions</text>
		<rect x="${centerX + 55}" y="${legendY}" width="10" height="10" fill="#5dade2" rx="2"/>
		<text x="${centerX + 70}" y="${legendY + 9}" font-size="10" fill="#666">Returns of Capital</text>
	</svg>`;
}

export function buildFundHeaderTemplate(targetUserData, logoBase64 = '') {
	const textColor = '#243b53';
	const userName = escapeStatementHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
	const companyName = escapeStatementHtml(clientData.company_name || '');

	const logoOrName = logoBase64 ? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">` : `<span class="company-name">${companyName}</span>`;

	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
		.company-logo { max-height: 50px; }
		.company-name { font-size: 20px; font-weight: bold; color: #1a5276; }
		.header-row { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
		.statement-title { font-size: 14px; font-weight: bold; color: ${textColor}; }
		.user-name { font-size: 12px; color: #666; }
	</style>
	<div class="header-container">
		<div style="text-align: center;">
			${logoOrName}
		</div>
		<div class="header-row">
			<div class="statement-title">Account Statement</div>
			<div class="user-name">${userName}</div>
		</div>
	</div>`;
}

export function buildFundFooterTemplate() {
	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #666; }
		.footer-inner { border-top: 1px solid #e2e8f0; padding: 8px 0; display: flex; justify-content: space-between; align-items: center; }
		.page-num { font-size: 10px; color: #243b53; }
	</style>
	<div class="footer-container">
		<div class="footer-inner">
			<div>Confidential - For recipient only</div>
			<div class="page-num">Page <span class="pageNumber"></span></div>
		</div>
	</div>`;
}
